import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogTitle,
  Button,
  DialogContent,
  DialogContentText,
  RadioGroup,
  FormControlLabel,
  Radio,
  DialogActions,
  useTheme,
  styled,
} from "@mui/material";
import axios from "axios";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { config } from "../../../config/config";
import {
  ProjectDetailContext,
  UseSnackbarContext,
} from "../Interfaces/ProjectDetailInterfaces";
import { projectARFileApis } from "../../../api/project";

interface ExportProps {
  open: boolean;
  close: () => void;
}

enum EExportSelection {
  LAS = "LAS",
  IFC = "IFC",
  PLY = "PLY",
}

const ExportARData = (exportProps: ExportProps) => {
  const { projectDetail } = useContext(ProjectDetailContext);
  const { openSuccess, openError } = useContext(UseSnackbarContext);

  const [whileLoading, setWhileLoading] = useState(false);

  const [currentSelection, setCurrentSelection] = useState<
    EExportSelection | ""
  >("");

  const [dlFileType, setDlFileType] = useState<string>("LAS"); //

  const [disabledSfM, setDisabledSfM] = useState<boolean>(true);
  const [disabledHelmert, setDisabledHelmert] = useState<boolean>(true);
  const [disabledReferencePoints, setDisabledReferencePoints] =
    useState<boolean>(true);
  const [disabledVerificationPoints, setDisabledVerificationPoints] =
    useState<boolean>(true);

  const [abortController, setAbortController] = useState<AbortController>(
    new AbortController()
  );
  const [disabledLINK, setDisabledLINK] = useState<boolean>(false);
  const [filemanagerURL, setFileManagerURL] = useState<string>("");

  useEffect(() => {
    axios
      .get(
        `${config.serverHost}/api/v1/project/${projectDetail?.id}/file-storage/node`,
        {
          withCredentials: true,
        }
      )
      .then((res) => {
        setFileManagerURL(
          `${process.env.REACT_APP_GROUPWARE_URL}/share?bucket=${res.data?.bucketId}&node=${res.data?.nodeId}`
        );
        setDisabledLINK(false);
      })
      .catch(() => {
        setDisabledLINK(true);
      });
  }, []);

  const initExportDialogData = () => {
    setCurrentSelection("");
    setDlFileType("LAS");
  };

  useEffect(() => {
    initExportDialogData();

    setAbortController(new AbortController());
  }, [exportProps.open]);

  const handleDownloadCancel = async () => {
    if (whileLoading) {
      openSuccess(t("CANCELLED"));
      abortController.abort();
    }
    exportProps.close();
    setWhileLoading(false);
  };

  const handleDownload = async () => {
    setWhileLoading(true);

    try {
      const response = await projectARFileApis.getFileUrl.get(
        projectDetail.id,
        currentSelection
      );
      console.log("Loading file response:", response);
      const presignedUrl = response.data;

      // Download the file using fetch for better control
      const fileResponse = await fetch(presignedUrl);

      if (!fileResponse.ok) {
        throw new Error(`HTTP error! status: ${fileResponse.status}`);
      }

      const blob = await fileResponse.blob();

      // Set the download filename based on selection
      let filename = `${projectDetail?.name || "export"}`;
      switch (currentSelection) {
        case EExportSelection.LAS:
          filename += ".las";
          break;
        case EExportSelection.IFC:
          filename += ".ifc";
          break;
        case EExportSelection.PLY:
          filename += ".ply";
          break;
        default:
          filename += ".file";
      }

      // Create blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;

      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(blobUrl);

      setWhileLoading(false);
      openSuccess(t("DOWNLOAD_COMPLETED"));
      console.log("File download completed:", filename);

      // Close dialog after successful download
      exportProps.close();
    } catch (error) {
      console.error("Download error:", error);
      setWhileLoading(false);
      openError(t("DOWNLOAD_ERROR"));
    }
  };

  const successDownload = () => {
    setWhileLoading(false);
    openSuccess(t("DOWNLOAD_COMPLETED"));
  };
  (window as any)["successDownload"] = successDownload;

  const errorDownload = () => {
    setWhileLoading(false);
    openError(t("DOWNLOAD_ERROR"));
  };
  (window as any)["errorDownload"] = errorDownload;

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customModalColor = {
    color: theme.palette.base.modal,
  };

  const customDialogBackgroundColor = {
    color: theme.palette.base.dialogBackground,
  };

  const customLoadingButtonColor = {
    background: theme.palette.primary.main,
    boxShadow: "none",
  };

  const CustomCanselButton = styled(Button)({
    background: theme.palette.base.modal,
    color: theme.palette.base.contrast,
    boxShadow: "none",
    "&:hover": {
      background: theme.palette.base.modal,
      color: theme.palette.base.contrast,
      boxShadow: "none",
    },
  });

  const selectExportSelection = (selection: EExportSelection) => {
    setCurrentSelection(selection);
  };

  return (
    <>
      <Dialog
        open={exportProps.open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          className: "overflow12",
          style: { background: customModalColor.color },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: customDialogBackgroundColor.color,
            },
          },
        }}
      >
        <DialogTitle id="alert-dialog-title" className="overflow13">
          {t("EXPORT_DATA")}
          <Button
            style={customColor}
            className="x-btn overflow14"
            onClick={handleDownloadCancel}
          >
            ×
          </Button>
          <div className="overflow15" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ textAlign: "left", ...customColor }}>
            {t("SELECT_FILE_EXPORT")}
          </DialogContentText>
          <RadioGroup>
            <FormControlLabel
              control={
                <Radio
                  checked={currentSelection === EExportSelection.LAS}
                  onChange={() => selectExportSelection(EExportSelection.LAS)}
                />
              }
              disabled={projectDetail?.las_file ? false : true}
              label={t("EXPORT_AR_POINT_CLOUD")}
              componentsProps={{
                typography: {
                  sx: {
                    lineHeight: "1.0",
                    width: "220px",
                    textAlign: "left",
                  },
                },
              }}
              style={{ margin: 0 }}
            />
            <FormControlLabel
              control={
                <Radio
                  checked={currentSelection === EExportSelection.IFC}
                  onChange={() => selectExportSelection(EExportSelection.IFC)}
                />
              }
              disabled={projectDetail?.ifc_file ? false : true}
              label={t("EXPORT_AR_DESIGN")}
              componentsProps={{
                typography: { sx: { lineHeight: "1.0", textAlign: "left" } },
              }}
              style={{ margin: 0 }}
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions className="overflow26">
          <CustomCanselButton
            size="small"
            variant="contained"
            onClick={handleDownloadCancel}
            className="overflow27 overflow28"
          >
            {t("CANCEL")}
          </CustomCanselButton>
          <LoadingButton
            style={whileLoading ? null : customLoadingButtonColor}
            size="small"
            disabled={currentSelection === "" || whileLoading}
            variant="contained"
            loading={whileLoading}
            onClick={handleDownload}
            color="primary"
            className="loading-white overflow27"
          >
            {t("EXPORT")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExportARData;
